// src/services/auth.service.ts

import apiService from './api';
import { API_CONFIG } from '../config/api.config';
import type {
    LoginCredentials,
    LoginResponse,
    ApiResponse,
    AuthState,
    User,
} from '../types/api.types';

/**
 * Authentication Service for AVEC Admin Dashboard
 * Handles login, logout, token management, and user session
 */

export class AuthService {
  private static instance: AuthService;
  private readonly STORAGE_KEY = 'avec_auth';

  private constructor() {
    // Listen for storage changes from other tabs
    window.addEventListener('storage', this.handleStorageChange.bind(this));
    // Listen for custom logout events
    window.addEventListener('auth:logout', this.handleLogoutEvent.bind(this));
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  /**
   * Handle storage changes from other tabs
   */
  private handleStorageChange(event: StorageEvent): void {
    if (event.key === this.STORAGE_KEY && event.newValue === null) {
      // Auth data was removed in another tab, trigger logout
      window.dispatchEvent(new CustomEvent('auth:logout'));
    }
  }

  /**
   * Handle custom logout event
   */
  private handleLogoutEvent(): void {
    // Clear any cached data or perform cleanup
    this.clearAuthData();
  }

  /**
   * Login user with email and password
   */
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      console.log('AuthService: Tentative de connexion avec:', credentials);
      
      const response: ApiResponse<LoginResponse> = await apiService.post(
        API_CONFIG.AUTH.LOGIN,
        credentials
      );

      console.log('AuthService: Réponse brute de l\'API:', response);

      if (response.success && response.data) {
        console.log('AuthService: Connexion réussie, stockage des données d\'authentification');
        
        // Store authentication data with improved validation
        this.storeAuthData(response.data);
        
        // Verify storage worked
        const storedData = this.getStoredAuthData();
        console.log('AuthService: Vérification - données stockées:', storedData);
        
        return response.data;
      } else {
        console.error('AuthService: Échec de la connexion - format de réponse invalide:', response);
        throw new Error(response.message || 'Échec de la connexion');
      }
    } catch (error: any) {
      console.error('AuthService: Erreur de connexion:', error);
      
      // Transform error for better user experience
      if (error.error?.code === '401') {
        throw new Error('Email ou mot de passe invalide');
      } else if (error.error?.code === 'NETWORK_ERROR') {
        throw new Error('Impossible de se connecter au serveur. Veuillez vérifier votre connexion internet.');
      }
      throw new Error(error.message || 'Échec de la connexion. Veuillez réessayer.');
    }
  }

  /**
   * Logout current user
   */
  async logout(): Promise<void> {
    console.log('AuthService: Début du processus de déconnexion');
    
    try {
      // Get token before clearing data
      const token = this.getAccessToken();
      
      if (token) {
        console.log('AuthService: Appel du endpoint de déconnexion du serveur');
        
        // Call logout endpoint to invalidate server-side session
        const response: ApiResponse<null> = await apiService.post(API_CONFIG.AUTH.LOGOUT);
        
        if (response.success) {
          console.log('AuthService: Déconnexion serveur réussie:', response.message);
        } else {
          console.warn('AuthService: La déconnexion serveur a retourné un non-succès:', response.message);
        }
      } else {
        console.log('AuthService: Aucun token trouvé, ignorant la déconnexion serveur');
      }
    } catch (error: any) {
      // Log error but don't prevent logout from completing
      console.error('AuthService: Erreur lors de la déconnexion serveur:', error);
      
      // Check if it's a 401 error (token already invalid)
      if (error.error?.code === '401') {
        console.log('AuthService: Token déjà invalide sur le serveur, continuation avec la déconnexion locale');
      } else {
        console.warn('AuthService: Échec de la déconnexion serveur, mais continuation avec la déconnexion locale');
      }
    } finally {
      // Always clear local auth data regardless of server response
      console.log('AuthService: Nettoyage des données d\'authentification locales');
      this.clearAuthData();
      
      // Trigger logout event for other components and tabs
      console.log('AuthService: Déclenchement de l\'événement de déconnexion');
      window.dispatchEvent(new CustomEvent('auth:logout'));
      
      console.log('AuthService: Processus de déconnexion terminé');
    }
  }

  /**
   * Force logout (for cases where server is unreachable)
   */
  async forceLogout(): Promise<void> {
    console.log('AuthService: Déconnexion forcée initiée');
    this.clearAuthData();
    window.dispatchEvent(new CustomEvent('auth:logout'));
  }

  /**
   * Get current authentication state
   */
  getAuthState(): AuthState {
    const authData = this.getStoredAuthData();
    
    if (!authData || !authData.user || !authData.tokens) {
      return {
        user: null,
        tokens: null,
        isAuthenticated: false,
        isLoading: false,
        permissions: null,
      };
    }

    // Validate token structure
    if (!this.isTokenFormatValid(authData.tokens.accessToken)) {
      console.warn('Format de token invalide détecté, nettoyage des données d\'authentification');
      this.clearAuthData();
      return {
        user: null,
        tokens: null,
        isAuthenticated: false,
        isLoading: false,
        permissions: null,
      };
    }

    // Check if tokens are expired (basic check)
    if (this.areTokensExpired(authData.tokens)) {
      console.warn('Tokens expirés détectés');
      // Don't clear immediately, let the refresh mechanism handle it
    }

    return {
      user: authData.user,
      tokens: authData.tokens,
      isAuthenticated: true,
      isLoading: false,
      permissions: authData.user.Staff?.permissions || authData.user.profile?.permissions || null,
    };
  }

  /**
   * Check if tokens are expired (basic JWT expiration check)
   */
  private areTokensExpired(tokens: { accessToken: string; refreshToken: string }): boolean {
    try {
      // Basic JWT expiration check for access token
      const refreshPayload = this.parseJwtPayload(tokens.refreshToken);
      
      const now = Math.floor(Date.now() / 1000);
      
      // If access token is expired but refresh token is still valid, that's OK
      // The refresh mechanism will handle it
      if (refreshPayload && refreshPayload.exp && refreshPayload.exp > now) {
        return false; // Refresh token is still valid
      }
      
      // Both tokens expired
      return true;
    } catch (error) {
      console.error('Erreur lors de la vérification de l\'expiration des tokens:', error);
      return true; // Assume expired if we can't parse
    }
  }

  /**
   * Parse JWT payload without verification (for expiration check only)
   */
  private parseJwtPayload(token: string): any {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      
      const payload = parts[1];
      const decoded = atob(payload);
      return JSON.parse(decoded);
    } catch (error) {
      return null;
    }
  }

  /**
   * Get current user
   */
  getCurrentUser(): User | null {
    const authState = this.getAuthState();
    return authState.user;
  }

  /**
   * Get current user permissions
   */
  getUserPermissions(): any {
    const authState = this.getAuthState();
    return authState.permissions;
  }

  /**
   * Check if user has specific permission
   */
  hasPermission(permission: string): boolean {
    const permissions = this.getUserPermissions();
    if (!permissions) return false;
    
    return !!permissions[permission];
  }

  /**
   * Check if user has any of the specified permissions
   */
  hasAnyPermission(permissionList: string[]): boolean {
    return permissionList.some(permission => this.hasPermission(permission));
  }

  /**
   * Check if user has all specified permissions
   */
  hasAllPermissions(permissionList: string[]): boolean {
    return permissionList.every(permission => this.hasPermission(permission));
  }

  /**
   * Get access token
   */
  getAccessToken(): string | null {
    const authData = this.getStoredAuthData();
    return authData?.tokens?.accessToken || null;
  }

  /**
   * Get refresh token
   */
  getRefreshToken(): string | null {
    const authData = this.getStoredAuthData();
    return authData?.tokens?.refreshToken || null;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const authState = this.getAuthState();
    return authState.isAuthenticated;
  }

  /**
   * Store authentication data in localStorage with improved validation
   */
  private storeAuthData(loginResponse: LoginResponse): void {
    try {
      // Validate the login response structure
      if (!loginResponse.user || !loginResponse.tokens) {
        throw new Error('Structure de réponse de connexion invalide');
      }

      if (!loginResponse.tokens.accessToken || !loginResponse.tokens.refreshToken) {
        throw new Error('Tokens manquants dans la réponse de connexion');
      }

      const authData = {
        user: loginResponse.user,
        tokens: {
          accessToken: loginResponse.tokens.accessToken,
          refreshToken: loginResponse.tokens.refreshToken,
        },
        loginTime: new Date().toISOString(),
        lastActivity: new Date().toISOString(),
      };
      
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(authData));
      
      // Verify storage was successful
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) {
        throw new Error('Échec du stockage des données d\'authentification');
      }
      
      console.log('Données d\'authentification stockées avec succès');
    } catch (error) {
      console.error('Erreur lors du stockage des données d\'authentification:', error);
      throw new Error('Échec du stockage des données d\'authentification');
    }
  }

  /**
   * Get stored authentication data
   */
  private getStoredAuthData(): any {
    try {
      const authData = localStorage.getItem(this.STORAGE_KEY);
      if (!authData) {
        return null;
      }
      
      const parsed = JSON.parse(authData);
      
      // Validate the structure
      if (!parsed.user || !parsed.tokens) {
        console.warn('Structure de données d\'authentification invalide, nettoyage');
        this.clearAuthData();
        return null;
      }

      // Update last activity
      parsed.lastActivity = new Date().toISOString();
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(parsed));
      
      return parsed;
    } catch (error) {
      console.error('Erreur lors de l\'analyse des données d\'authentification:', error);
      this.clearAuthData();
      return null;
    }
  }

  /**
   * Clear authentication data
   */
  private clearAuthData(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      console.log('Données d\'authentification nettoyées avec succès');
    } catch (error) {
      console.error('Erreur lors du nettoyage des données d\'authentification:', error);
    }
  }

  /**
   * Basic token format validation (instead of full JWT parsing)
   */
  private isTokenFormatValid(token: string): boolean {
    if (!token || typeof token !== 'string') {
      return false;
    }
    
    // Basic JWT format check (should have 3 parts separated by dots)
    const parts = token.split('.');
    return parts.length === 3;
  }

  /**
   * Update user profile data
   */
  updateUserProfile(updatedUser: Partial<User>): void {
    const authData = this.getStoredAuthData();
    if (authData && authData.user) {
      authData.user = { ...authData.user, ...updatedUser };
      authData.lastActivity = new Date().toISOString();
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(authData));
    }
  }

  /**
   * Get user role
   */
  getUserRole(): string | null {
    const user = this.getCurrentUser();
    return user?.userType || null;
  }

  /**
   * Check if user is admin
   */
  isAdmin(): boolean {
    const role = this.getUserRole();
    return role === 'administrator' || role === 'admin';
  }

  /**
   * Get user's approval limits
   */
  getApprovalLimits(): any {
    const permissions = this.getUserPermissions();
    return permissions?.approvalLimits || null;
  }

  /**
   * Check if user can approve credit of given amount
   */
  canApproveCredit(amount: number): boolean {
    const limits = this.getApprovalLimits();
    if (!limits || !this.hasPermission('credits.approve')) return false;
    
    if (limits.credit === 'unlimited') return true;
    
    const limit = parseFloat(limits.credit);
    return !isNaN(limit) && amount <= limit;
  }

  /**
   * Get session info for debugging
   */
  getSessionInfo(): any {
    const authData = this.getStoredAuthData();
    if (!authData) return null;
    
    return {
      user: authData.user?.email,
      userType: authData.user?.userType,
      loginTime: authData.loginTime,
      lastActivity: authData.lastActivity,
      hasToken: !!authData.tokens?.accessToken,
      hasRefreshToken: !!authData.tokens?.refreshToken,
      tokenValid: this.isTokenFormatValid(authData.tokens?.accessToken || ''),
      refreshTokenValid: this.isTokenFormatValid(authData.tokens?.refreshToken || ''),
    };
  }

  /**
   * Manual token refresh for testing
   */
  async refreshToken(): Promise<boolean> {
    try {
      return await apiService.manualRefreshToken();
    } catch (error) {
      console.error('Échec du rafraîchissement manuel du token:', error);
      return false;
    }
  }

  /**
   * Check session validity
   */
  isSessionValid(): boolean {
    const authData = this.getStoredAuthData();
    if (!authData) return false;

    // Check if tokens exist and are properly formatted
    if (!authData.tokens?.accessToken || !authData.tokens?.refreshToken) {
      return false;
    }

    if (!this.isTokenFormatValid(authData.tokens.accessToken) || 
        !this.isTokenFormatValid(authData.tokens.refreshToken)) {
      return false;
    }

    // Check if tokens are expired
    if (this.areTokensExpired(authData.tokens)) {
      return false;
    }

    return true;
  }
}

// Export singleton instance
const authService = AuthService.getInstance();
export default authService;